// Offline mode: wording for the banner and the Offline mode page. Pure functions, no React.

const DOMAIN_LABELS = {
  menu_dishes: 'Menu and prices',
  category_listing: 'Categories',
  menu_categories: 'Menu groups',
  floor_plan: 'Floor plan',
  seating: 'Tables',
  occupied: 'Table status',
  servers: 'Servers',
  staff_settings: 'Staff login setting',
  payment_modes: 'Payment methods',
  global_surcharges: 'Surcharges and tips',
  printers: 'Printers',
  promotions: 'Promotions',
  pos_settings: 'POS settings',
  custom_product: 'Custom item',
  weighted_products: 'Weighted items',
  half_config: 'Half and half',
  delivery_fees: 'Delivery fees',
  loyalty: 'Loyalty',
  time_slots: 'Time slots',
  staff_pin: 'Staff PIN credentials',
};

// What the till relies on while offline comes first; the rest is listed under "More data".
const MAIN = ['menu_dishes', 'category_listing', 'floor_plan', 'seating', 'servers', 'payment_modes', 'global_surcharges', 'staff_pin'];

const DAY = 86400;

/** "Not downloaded", "Just now", "12 min ago", "5 h ago", "3 days ago" (-1 means never downloaded). */
export function ageText(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return 'Not downloaded';
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < DAY) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / DAY);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** 'bad' = never downloaded, 'warn' = older than a day, else 'ok'. */
export const ageTone = (seconds) => (!Number.isFinite(Number(seconds)) || Number(seconds) < 0 ? 'bad' : Number(seconds) > DAY ? 'warn' : 'ok');

/** Rows for the data-freshness list: { key, label, text, tone, main }, the important ones first. Unknown domains keep their key as the label. */
export function freshnessRows(ages) {
  const rows = Object.entries(ages && typeof ages === 'object' ? ages : {})
    .filter(([k]) => k !== 'staff') // the optional staff list endpoint: the PIN credentials row covers it
    .map(([key, v]) => ({ key, label: DOMAIN_LABELS[key] || key.replace(/_/g, ' '), text: ageText(v), tone: ageTone(v), main: MAIN.includes(key) }));
  rows.sort((a, b) => (a.main === b.main ? a.label.localeCompare(b.label) : a.main ? -1 : 1));
  return rows;
}

export function durationText(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min`;
  if (s < DAY) return `${Math.floor(s / 3600)} h`;
  const d = Math.floor(s / DAY);
  return `${d} day${d === 1 ? '' : 's'}`;
}

/** What the page shows for the device registration: { status, label, tone, action, help }. */
export function deviceView(state, device) {
  const status = device?.status || state?.deviceStatus || 'unregistered';
  switch (status) {
    case 'active':
      return { status, label: 'Registered', tone: 'ok', action: null, help: 'Staff PINs work offline on this device. If an admin revokes it on the server, PIN login stops here until it is registered again; orders and uploads are never touched.' };
    case 'revoked':
      return { status, label: 'Revoked by the server', tone: 'bad', action: 'register', help: 'The server cut this device off (revoked, or the account password changed), so offline PINs were removed. Register it again with an admin PIN. Orders waiting to upload are safe.' };
    case 'needs_reauth':
      return { status, label: 'Credential expired', tone: 'warn', action: 'reissue', help: "This device's offline credential expired. An admin PIN renews it." };
    default:
      return { status: 'unregistered', label: 'Not registered', tone: state?.staffLoginEnabled === true ? 'bad' : 'warn', action: 'register', help: 'Registering gives this device its own credential so staff PINs keep working with no internet, also after the app is restarted.' };
  }
}

/** One line about the staff PIN credentials. */
export function staffText(state, staff) {
  const ready = state?.capabilities?.staffPin === true;
  if (state?.staffLoginEnabled === false) return 'Staff login is off for this restaurant.';
  if (!ready) return 'No PIN credentials on this device.';
  const n = Number(staff?.credentials);
  const left = Number(staff?.maxAgeSeconds) - Number(staff?.ageSeconds);
  const base = `${Number.isFinite(n) && n > 0 ? `${n} staff PIN${n === 1 ? '' : 's'}` : 'PIN credentials'} ready`;
  return Number.isFinite(left) && left > 0 ? `${base}, valid offline for ${durationText(left)} more` : base;
}

/** Upload line: "3 waiting to upload", "2 need attention", "Last upload 4 min ago". */
export function uploadText(state, nowMs = Date.now()) {
  const parts = [];
  if (state?.outboxCount > 0) parts.push(`${state.outboxCount} waiting to upload`);
  if (state?.parkedCount > 0) parts.push(`${state.parkedCount} need attention`);
  if (state?.lastUploadAt) parts.push(`Last upload ${ageText(Math.max(0, Math.floor((nowMs - Number(state.lastUploadAt)) / 1000)))}`);
  return parts.length ? parts.join(' · ') : 'Nothing waiting to upload';
}

/** A server-parked record -> a line for the manager. */
export function parkedLine(p) {
  const what =
    p.entity === 'customer' ? `Customer ${p.label || ''}`.trim() : p.entity === 'settlement' ? `Payment, ${p.label || 'a table'}`.trim() : `Order ${p.label || ''}`.trim();
  const why = [p.code, p.field].filter(Boolean).join(' · ');
  return { title: what, detail: p.message || why || 'The server did not accept it', code: why };
}

/** Where the PIN credentials came from (the engine's staff.source). */
export function sourceText(source) {
  if (source === 'server:bootstrap') return 'Downloaded from the server';
  if (source === 'server:delta') return 'Downloaded from the server (updated)';
  if (source === 'import') return 'Loaded by developer tooling, not by the server';
  return '';
}

/** The engine's mode as one phrase for the Offline mode page. */
export function modeText(mode) {
  if (mode === 'forced_offline') return 'Offline (switched on by hand)';
  if (mode === 'offline') return 'No internet';
  return 'Online';
}

/** The banner's explanation while offline. */
export const bannerDetail = (mode) =>
  `${mode === 'forced_offline' ? 'Switched on by hand' : 'No internet'} - orders are saved on this device and upload when it is back online`;

/** The banner's counter, or '' when nothing is waiting. */
export const waitingText = (state) => (state?.outboxCount > 0 ? `${state.outboxCount} waiting to upload` : '');

/**
 * The words for an offline "not available" answer (HTTP 501 from the engine, body { offline: true, reason }), or null when the error is
 * not that. A table or order that was opened in the cloud before the outage is the case staff meet: items cannot be added to it until the
 * connection is back, and it can be paid (cash, or a card payment recorded by hand) only while the device holds a current copy of its bill.
 */
export function offlineRefusal(err) {
  const res = err && err.response;
  if (!res || res.status !== 501 || !res.data || res.data.offline !== true) return null;
  const reason = String(res.data.reason || '');
  if (/^table .*opened in the cloud/i.test(reason)) {
    return "This table was opened before offline mode and its bill isn't on this device, so it can't be added to or paid until the connection is back. Use another table, or wait for the connection.";
  }
  if (/^adding to this table offline is not possible: /i.test(reason)) {
    const why = reason.replace(/^adding to this table offline is not possible: /i, '');
    return `Items can't be added to this table offline: ${why}. Take payment as it is, or wait for the connection.`;
  }
  if (/copy of this bill is older/i.test(reason)) {
    return "This bill changed in the cloud after this device last updated it, so it can't be paid offline yet. Give it a moment to refresh, or wait for the connection.";
  }
  if (/tip was not kept/i.test(reason)) {
    return "That tip isn't available offline for this table. Choose another tip, or wait for the connection.";
  }
  if (/^only cash and (manually|card)/i.test(reason)) {
    return 'Offline you can take cash, or a card payment recorded by hand. A card payment on the terminal needs the connection.';
  }
  if (/^order .*lives in the cloud/i.test(reason)) {
    return "This order was opened before offline mode, so it can't be changed or paid until the connection is back.";
  }
  return res.data.message || "This isn't available offline.";
}

/** A kept copy of a bill older than this is flagged as possibly out of date (decision D7 of the open-tables design). */
export const KEPT_BILL_STALE_MS = 5 * 60 * 1000;

/**
 * What to tell staff about a table's bill that the offline engine answered from the copy it keeps (response headers `X-OA-Source:
 * open-table-copy` and `X-OA-Age`, seconds; `X-OA-As-Of`, epoch ms, when exposed), or null for any other answer. { time, stale, text }.
 */
export function keptBillNotice(headers, nowMs = Date.now()) {
  if (!headers) return null;
  const h = {};
  Object.keys(headers).forEach((k) => {
    h[k.toLowerCase()] = headers[k];
  });
  if (h['x-oa-source'] !== 'open-table-copy') return null;
  // the page can only read the headers the loopback server exposes (X-OA-Mode, -Source, -Age, -Refreshing): the age in seconds is one of them, so the time
  // of the copy is worked out from it; X-OA-As-Of (epoch ms) is used when a host exposes it
  const age = Number(h['x-oa-age']);
  const asOf = Number(h['x-oa-as-of']) > 0 ? Number(h['x-oa-as-of']) : Number.isFinite(age) && h['x-oa-age'] !== undefined && h['x-oa-age'] !== '' ? nowMs - Math.max(0, age) * 1000 : NaN;
  if (!Number.isFinite(asOf) || asOf <= 0) return null;
  const time = new Date(asOf).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const stale = nowMs - asOf > KEPT_BILL_STALE_MS;
  const text = `This table was opened before offline mode. Its bill is shown as of ${time}. You can add items and take cash, or a card payment recorded by hand; everything is sent to the cloud when the connection is back.`;
  return { time, stale, text, staleText: stale ? 'This bill may be out of date: check with the kitchen before relying on it.' : '' };
}
